import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "antd";
import { PageBackButton } from "@/shared/components/PageBackButton";
import { getPatientApi } from "@/portals/admin/api/adminPatientsApi";
import { adminQueryKeys } from "@/portals/admin/api/adminQueryKeys";
import { useAdminPatients } from "@/portals/admin/context/AdminPatientsContext";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import { AssignedClinicianSection } from "@/features/admin/users/components/AssignedClinicianSection";
import { formatDate } from "@/features/admin/clinicians/components/formatDate";

/** /admin/users/:patientId — patient profile summary + Assigned clinician (Module 4). */
export function PatientDetailPage() {
  const { patientId = "" } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  // Cached per patient. Opened from the Patients list, the row already on screen
  // there is shown right away while the full record loads in the background.
  const { patients } = useAdminPatients();
  const cached = useCachedQuery(adminQueryKeys.patient(patientId), () => getPatientApi(patientId));
  const patient = cached.data ?? patients.find((p) => p.id === patientId) ?? null;
  const loading = cached.loading && !patient;

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
      <PageBackButton onClick={() => navigate("/admin/users")} ariaLabel="Back to patients" className="!mb-5" />
      <div className="grid gap-5">
        <section aria-label="Patient" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
          {loading ? (
            <Skeleton active title paragraph={{ rows: 2 }} />
          ) : patient ? (
            <>
              <p className="text-lg font-extrabold text-[#0A2947]">{patient.name}</p>
              <p className="text-xs text-[#4B5563]">{patient.email}</p>
              <dl className="!mb-0 !mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[
                  { label: "Phone", value: patient.phone || "—" },
                  { label: "Date of birth", value: formatDate(patient.dob) },
                  { label: "Status", value: patient.is_disabled ? "Disabled" : "Active" },
                  { label: "Registered", value: formatDate(patient.created_at) },
                ].map(({ label, value }) => (
                  <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                    <dt className="text-xs text-[#9CA3AF]">{label}</dt>
                    <dd className="!m-0 mt-1 text-sm font-semibold text-[#0A2947]">{value}</dd>
                  </div>
                ))}
              </dl>
            </>
          ) : (
            <p className="text-sm text-[#9CA3AF]">Patient details are unavailable.</p>
          )}
        </section>
        <AssignedClinicianSection patientId={patientId} />
      </div>
    </div>
  );
}
